import { type ElementHorizontalAlignment } from '@blaze-shared/elements-domain';
import { toInt, type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

import { HorizontalAlignmentStyle, LabelPositionStyle } from '../../../ai/models/styles';
import {
  MAX_FIXED_TAB_WIDTH,
  MIN_FIXED_TAB_WIDTH,
  type TabIconPosition,
  type TabSize,
  type TabsTemplate,
} from '../tabs.model';

import { type TabSizeStyle, type TabTemplateStyle } from './tabs.definition';

export const DEFAULT_FIXED_TAB_WIDTH = 220;

export interface TabSizeSettings {
  tabSize: TabSize;
  tabWidth?: ValueWithUnitOfMeasure;
}

export function toEngineTemplate(style: TabTemplateStyle): TabsTemplate {
  return style.$type;
}

export function toTemplateStyle(template: TabsTemplate): TabTemplateStyle {
  return { $type: template };
}

export function toEngineTabSize(style: TabSizeStyle): TabSizeSettings {
  switch (style.$type) {
    case 'fit':
      return { tabSize: 'hug' };
    case 'full':
      return { tabSize: 'fill' };
    case 'fixed':
      return { tabSize: 'fixed', tabWidth: `${clampFixedTabWidth(style.width)}px` };
  }
}

export function toTabSizeStyle(tabSize: TabSize, tabWidth: ValueWithUnitOfMeasure): TabSizeStyle {
  switch (tabSize) {
    case 'hug':
      return { $type: 'fit' };
    case 'fill':
      return { $type: 'full' };
    case 'fixed':
      return { $type: 'fixed', width: toInt(tabWidth, DEFAULT_FIXED_TAB_WIDTH) };
  }
}

export function toEngineAlignment(style: HorizontalAlignmentStyle): ElementHorizontalAlignment {
  switch (style) {
    case HorizontalAlignmentStyle.Left:
      return 'left';
    case HorizontalAlignmentStyle.Center:
      return 'center';
    case HorizontalAlignmentStyle.Right:
      return 'right';
  }
}

export function toAlignmentStyle(align: ElementHorizontalAlignment): HorizontalAlignmentStyle {
  switch (align) {
    case 'left':
      return HorizontalAlignmentStyle.Left;
    case 'right':
      return HorizontalAlignmentStyle.Right;
    case 'center':
    case 'justify':
      return HorizontalAlignmentStyle.Center;
  }
}

export function toEngineIconPosition(style: LabelPositionStyle): TabIconPosition {
  switch (style) {
    case LabelPositionStyle.Left:
      return 'left';
    case LabelPositionStyle.Top:
      return 'top';
  }
}

export function toIconPositionStyle(position: TabIconPosition): LabelPositionStyle {
  switch (position) {
    case 'left':
      return LabelPositionStyle.Left;
    case 'top':
      return LabelPositionStyle.Top;
  }
}

function clampFixedTabWidth(width: number | undefined): number {
  return Math.min(
    MAX_FIXED_TAB_WIDTH,
    Math.max(MIN_FIXED_TAB_WIDTH, width ?? DEFAULT_FIXED_TAB_WIDTH),
  );
}
